import React, { useEffect, useMemo, useState } from "react";
import { Plus, ChevronDown, X, UsersRound, Cpu, Wrench } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";

const ROLE_DEFS = [
  { key: "product_managers", label: "Product Manager", Icon: UsersRound,    chip: "bg-violet-100 text-violet-800 border-violet-200" },
  { key: "ai_ml_engineers",  label: "AI/ML Engineer",   Icon: Cpu,           chip: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "integrators",      label: "Integrator",       Icon: Wrench,        chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];

function MemberChipsPicker({ label, Icon, chipClass, members, selected, onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const memberByEmail = useMemo(() => {
    const m = new Map();
    for (const x of members) m.set(x.email, x);
    return m;
  }, [members]);
  const filtered = useMemo(
    () => (query ? members.filter((m) => (m.name || "").toLowerCase().includes(query.toLowerCase()) || (m.email || "").toLowerCase().includes(query.toLowerCase())) : members),
    [members, query],
  );
  const toggle = (email) => {
    if (selected.includes(email)) onChange(selected.filter((e) => e !== email));
    else onChange([...selected, email]);
  };
  const remove = (email, e) => {
    e.stopPropagation();
    onChange(selected.filter((x) => x !== email));
  };

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-1.5 shrink-0 w-[140px]">
        <Icon className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      </div>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
        <PopoverTrigger asChild>
          <button
            disabled={disabled}
            className={`min-h-[34px] flex-1 px-2 py-1 rounded-md border flex flex-wrap items-center gap-1 text-left transition-colors ${
              selected.length
                ? "bg-white border-slate-200 hover:border-blue-300"
                : "bg-slate-50 border-dashed border-slate-300 hover:border-blue-300 hover:bg-white"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
            data-testid={`${testid}-trigger`}
          >
            {selected.length === 0 ? (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Plus className="h-3.5 w-3.5" /> Add {label.toLowerCase()}
              </span>
            ) : (
              <>
                {selected.map((email) => {
                  const m = memberByEmail.get(email);
                  const name = m?.name || email.split("@")[0];
                  return (
                    <span key={email} className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded border ${chipClass}`}>
                      {name}
                      {!disabled && (
                        <span
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => remove(email, e)}
                          className="hover:text-rose-600 -mr-0.5 cursor-pointer"
                          aria-label={`Remove ${name}`}
                        >
                          <X className="h-3 w-3" />
                        </span>
                      )}
                    </span>
                  );
                })}
              </>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 ml-auto shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-slate-100">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members…"
              className="h-8 text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">No members found.</div>
            ) : (
              filtered.map((m) => {
                const checked = selected.includes(m.email);
                return (
                  <div
                    key={m.email || m.id}
                    role="option"
                    tabIndex={0}
                    onClick={() => toggle(m.email)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(m.email); } }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
                    aria-selected={checked}
                  >
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-800 truncate">{m.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{m.email}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * AgentOwnersBar
 * - agentName: the master agent_name (Strategy, Design, etc.). When falsy or "__all__", the bar is hidden.
 * - members: workspace members.
 * - readOnly (default false): when true, chips render but multi-select cannot be edited.
 */
export default function AgentOwnersBar({ agentName, members = [], readOnly = false }) {
  const [allOwners, setAllOwners] = useState([]); // server-side cache
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/agent-category-owners");
        if (alive) setAllOwners(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (!agentName || agentName === "__all__") return null;

  const current = allOwners.find((o) => o.agent_name === agentName) || {
    agent_name: agentName,
    product_managers: [],
    ai_ml_engineers: [],
    integrators: [],
  };

  const persist = async (patch) => {
    const next = { ...current, ...patch };
    setAllOwners((prev) => {
      const idx = prev.findIndex((o) => o.agent_name === agentName);
      if (idx === -1) return [...prev, next];
      const copy = prev.slice();
      copy[idx] = next;
      return copy;
    });
    try {
      const { data } = await api.put(`/agent-category-owners/${encodeURIComponent(agentName)}`, patch);
      setAllOwners((prev) => {
        const idx = prev.findIndex((o) => o.agent_name === agentName);
        const copy = prev.slice();
        if (idx === -1) copy.push(data); else copy[idx] = data;
        return copy;
      });
    } catch { toast.error("Failed to save owners"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-3" data-testid={`agent-owners-bar-${agentName}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
          Owners for <span className="text-blue-700">{agentName}</span>
        </p>
        {readOnly && <span className="text-[10px] text-slate-400 italic">read-only</span>}
      </div>
      <div className="flex items-stretch gap-3 flex-wrap">
        {ROLE_DEFS.map((rd) => (
          <MemberChipsPicker
            key={rd.key}
            label={rd.label}
            Icon={rd.Icon}
            chipClass={rd.chip}
            members={members}
            selected={current[rd.key] || []}
            onChange={(next) => !readOnly && persist({ [rd.key]: next })}
            disabled={readOnly || loading}
            testid={`agent-owners-${agentName}-${rd.key}`}
          />
        ))}
      </div>
    </div>
  );
}
